import { assign, Interpreter, Machine, State } from 'xstate';
import WindowService from '../../services/WindowService';
import { CustomerDataContract } from '../../types/Customer';
import { PayPalOnApproveData } from '../../types/PayPal';

interface CheckoutStateSchema {
  states: {
    Start: Record<string, unknown>;
    Loading: Record<string, unknown>;
    CheckAuth: Record<string, unknown>;
    LoadWallet: {
      initial: 'Loading';
      states: {
        Loading: Record<string, unknown>;
        Complete: Record<string, unknown>;
        Error: Record<string, unknown>;
      };
    };
    Guest: {
      states: {
        WalletValidation: Record<string, unknown>;
        Shipping: {
          type: 'parallel';
          states: {
            Contact: {
              initial: 'Edit';
              states: {
                Summary: Record<string, unknown>;
                Edit: Record<string, unknown>;
                hist: Record<string, unknown>;
              };
            };
            Address: {
              initial: 'Add';
              states: {
                Add: Record<string, unknown>;
                Edit: Record<string, unknown>;
                Summary: Record<string, unknown>;
                hist: Record<string, unknown>;
              };
            };
            Methods: {
              initial: 'NotFetched';
              states: {
                NotFetched: Record<string, unknown>;
                List: Record<string, unknown>;
                Summary: Record<string, unknown>;
                hist: Record<string, unknown>;
              };
            };
          };
        };
        Payment: {
          initial: 'Default';
          states: {
            Default: Record<string, unknown>;
            ThreeDSPending: Record<string, unknown>;
            StripeWallet: Record<string, unknown>;
          };
        };
      };
    };
    OneClick: {
      states: {
        Loading: Record<string, unknown>;
        Start: {
          states: {
            Valid: Record<string, unknown>;
            MissingFields: Record<string, unknown>;
            hist: Record<string, unknown>;
            ThreeDSPending: Record<string, unknown>;
          };
        };
        WalletValidation: {
          states: {
            CheckAll: Record<string, unknown>;
            PartialShipping: {
              states: {
                Edit: Record<string, unknown>;
              };
            };
            ChangeShippingMethod: {
              states: {
                Fetching: Record<string, unknown>;
                Fetched: Record<string, unknown>;
              };
            };
            PartialBilling: {
              states: {
                Edit: Record<string, unknown>;
              };
            };
          };
        };
        ShippingAddress: {
          states: {
            List: Record<string, unknown>;
            Add: Record<string, unknown>;
          };
        };
        BillingAddress: {
          states: {
            List: Record<string, unknown>;
            Add: Record<string, unknown>;
          };
        };
        Payment: Record<string, unknown>;
      };
    };
    PayPal: {
      states: {
        UserStatus: {
          states: {
            CheckAuth: Record<string, unknown>;
            Guest: Record<string, unknown>;
            Registered: Record<string, unknown>;
          };
        };
        Checkout: {
          states: {
            Loading: Record<string, unknown>;
            MissingFields: Record<string, unknown>;
            ShippingEdit: Record<string, unknown>;
            Start: Record<string, unknown>;
            Payment: Record<string, unknown>;
            Exit: Record<string, unknown>;
          };
        };
      };
    };
    AmazonPay: {
      states: {
        UserStatus: {
          states: {
            CheckAuth: Record<string, unknown>;
            Guest: Record<string, unknown>;
            Registered: Record<string, unknown>;
          };
        };
        Checkout: {
          states: {
            Loading: Record<string, unknown>;
            Start: Record<string, unknown>;
            Payment: Record<string, unknown>;
            Exit: Record<string, unknown>;
          };
        };
      };
    };
    Error: Record<string, unknown>;
    ThankYou: Record<string, unknown>;
  };
}

export interface StripeWalletContext {
  token: string;
  type: string;
}

export interface CheckoutContext {
  customer: CustomerDataContract | null;
  orderId: string | null;
  storeUrl: string | null;
  paypalData: PayPalOnApproveData | null;
  amazonPayToken: string | null;
  stripeWallet: StripeWalletContext | null;
}

export type CheckoutEvent =
  | { type: 'CONFIG_FETCHING'; storeUrl: string }
  | {
      type: 'CONFIG_FETCHED';
      customer: CustomerDataContract;
      paypalParams: PayPalOnApproveData | null;
      amazonPayToken: string | null;
      stripeWallet: StripeWalletContext | null;
    }
  | { type: 'USER_LOGIN'; customer: CustomerDataContract }
  | { type: 'STRIPE_WALLET_CHOSEN'; stripeWallet: StripeWalletContext }
  | { type: 'AMAZONPAY_ON_AUTHORIZE'; amazonPayToken: string }
  | { type: 'AMAZONPAY_INITIALIZED' }
  | { type: 'AMAZONPAY_EXIT'; amazonPayToken: null }
  | { type: 'CONTACT_SAVED' }
  | { type: 'EDIT_CONTACT' }
  | { type: 'SHIPPING_ADDRESS_CHANGED' }
  | { type: 'SHIPPING_ADDRESS_SAVED' }
  | { type: 'EDIT_SHIPPING' }
  | { type: 'EDIT_SHIPPING_CANCELED' }
  | { type: 'BILLING_ADDRESS_SAVED' }
  | { type: 'EDIT_BILLING' }
  | { type: 'EDIT_BILLING_CANCELED' }
  | { type: 'SHIPPING_METHODS_FETCHED' }
  | { type: 'EDIT_SHIPPING_METHODS' }
  | { type: 'CONTINUE_TO_PAYMENT' }
  | { type: 'SHIPPING_METHOD_SELECTED'; shippingFieldsCompleted?: boolean }
  | { type: 'ORDER_PLACED'; orderId: string }
  | { type: 'ORDER_ERROR' }
  | { type: 'RETRY' }
  | { type: 'USER_LOGOUT' }
  | { type: 'ADDRESS_SELECTED' }
  | { type: 'ADD_SHIPPING_ADDRESS' }
  | { type: 'LIST_SHIPPING_ADDRESSES' }
  | { type: 'ADD_BILLING_ADDRESS' }
  | { type: 'LIST_BILLING_ADDRESSES' }
  | { type: 'ADDRESS_ADDED' }
  | { type: 'ADD_ADDRESS_CANCEL' }
  | { type: 'EDIT_PAYMENT' }
  | { type: 'EDIT_PAYMENT_COMPLETE' }
  | { type: 'PAYPAL_ON_APPROVED'; paypalParams: PayPalOnApproveData }
  | { type: 'PAYPAL_DATA_LOADED_WITH_MISSING_FIELDS' }
  | { type: 'CART_UPDATED_WITH_MISSING_FIELDS' }
  | { type: 'PAYPAL_DATA_LOADED' }
  | { type: 'PAYPAL_EXIT'; paypalParams: null }
  | { type: 'CART_UPDATED' }
  | { type: 'SPREEDLY_3DS_LIFECYCLE_STARTED' }
  | { type: 'WALLET_LOADED' }
  | { type: 'WALLET_ERROR' }
  | { type: 'WALLET_IS_VALID' }
  | { type: 'WALLET_NEEDS_SHIPPING' }
  | { type: 'WALLET_NEEDS_BILLING' };

export type CheckoutState = State<CheckoutContext, CheckoutEvent>;
export type CheckoutInterpreter = Interpreter<
  CheckoutContext,
  CheckoutStateSchema,
  CheckoutEvent
>;

const checkoutMachine = Machine<
  CheckoutContext,
  CheckoutStateSchema,
  CheckoutEvent
>(
  {
    id: 'checkout',
    context: {
      customer: null,
      orderId: null,
      storeUrl: null,
      paypalData: null,
      amazonPayToken: null,
      stripeWallet: null,
    },
    initial: 'Start',
    states: {
      Start: {
        on: {
          CONFIG_FETCHING: {
            actions: ['setStoreUrl'],
            target: 'Loading',
          },
        },
      },
      Loading: {
        on: {
          CONFIG_FETCHED: {
            actions: [
              'setCustomer',
              'setPayPalData',
              'setAmazonPayToken',
              'setStripeWallet',
            ],
            target: 'CheckAuth',
          },
        },
      },
      CheckAuth: {
        id: 'CheckAuth',
        always: [
          {
            cond: 'isUsingPayPal',
            target: 'PayPal',
          },
          {
            cond: 'isUsingAmazonPay',
            target: 'AmazonPay',
          },
          {
            cond: 'isUsingWallet',
            target: 'LoadWallet',
          },
          {
            cond: 'isLoggedIn',
            target: 'OneClick',
          },
          {
            target: 'Guest',
          },
        ],
      },
      LoadWallet: {
        id: 'LoadWallet',
        initial: 'Loading',
        states: {
          Loading: {
            id: 'LoadWallet.Loading',
            on: {
              WALLET_LOADED: {
                target: 'Complete',
              },
              WALLET_ERROR: {
                target: 'Error',
              },
            },
          },
          Complete: {
            id: 'LoadWallet.Complete',
            always: [
              {
                cond: 'isLoggedIn',
                target: '#OneClick.WalletValidation',
              },
              {
                target: '#Guest.WalletValidation',
              },
            ],
          },
          Error: {
            id: 'LoadWallet.Error',
            always: [
              {
                cond: 'isLoggedIn',
                target: '#OneClick',
              },
              {
                target: '#Guest',
              },
            ],
          },
        },
      },
      Guest: {
        id: 'Guest',
        initial: 'Shipping',
        on: {
          USER_LOGIN: {
            actions: ['setCustomer'],
            target: '#OneClick',
          },
          AMAZONPAY_ON_AUTHORIZE: {
            actions: ['setAmazonPayToken'],
            target: '#AmazonPay',
          },
          PAYPAL_ON_APPROVED: {
            actions: ['setPayPalData'],
            target: '#PayPal',
          },
        },
        states: {
          WalletValidation: {
            on: {
              WALLET_IS_VALID: '#Guest.Payment.StripeWallet',
              WALLET_NEEDS_SHIPPING: '#Guest.Shipping',
              WALLET_NEEDS_BILLING: '#Guest.Payment.StripeWallet',
            },
          },
          Shipping: {
            id: 'Guest.Shipping',
            type: 'parallel',
            on: {
              STRIPE_WALLET_CHOSEN: {
                actions: ['setStripeWallet'],
                target: '#Guest.WalletValidation',
              },
            },
            states: {
              Contact: {
                initial: 'Edit',
                states: {
                  Summary: {
                    on: {
                      EDIT_CONTACT: {
                        target: 'Edit',
                      },
                      CONTINUE_TO_PAYMENT: [
                        {
                          cond: 'isUsingWallet',
                          target: '#Guest.Payment.StripeWallet',
                        },
                        {
                          target: '#Guest.Payment',
                        },
                      ],
                    },
                  },
                  Edit: {
                    on: {
                      CONTACT_SAVED: {
                        target: 'Summary',
                      },
                      CONTINUE_TO_PAYMENT: [
                        {
                          cond: 'isUsingWallet',
                          target: '#Guest.Payment.StripeWallet',
                        },
                        {
                          target: '#Guest.Payment',
                        },
                      ],
                    },
                  },
                  hist: {
                    type: 'history',
                  },
                },
              },
              Address: {
                initial: 'Add',
                states: {
                  Add: {
                    on: {
                      SHIPPING_ADDRESS_SAVED: {
                        target: 'Summary',
                      },
                    },
                  },
                  Summary: {
                    on: {
                      EDIT_SHIPPING: {
                        target: 'Edit',
                      },
                      CONTINUE_TO_PAYMENT: [
                        {
                          cond: 'isUsingWallet',
                          target: '#Guest.Payment.StripeWallet',
                        },
                        {
                          target: '#Guest.Payment',
                        },
                      ],
                    },
                  },
                  Edit: {
                    on: {
                      SHIPPING_ADDRESS_SAVED: {
                        target: 'Summary',
                      },
                    },
                  },
                  hist: {
                    type: 'history',
                  },
                },
              },
              Methods: {
                initial: 'NotFetched',
                states: {
                  NotFetched: {
                    on: {
                      SHIPPING_METHODS_FETCHED: {
                        target: 'List',
                      },
                    },
                  },
                  Summary: {
                    on: {
                      EDIT_SHIPPING_METHODS: {
                        target: 'List',
                      },
                      SHIPPING_ADDRESS_CHANGED: {
                        target: 'NotFetched',
                      },
                      CONTINUE_TO_PAYMENT: [
                        {
                          cond: 'isUsingWallet',
                          target: '#Guest.Payment.StripeWallet',
                        },
                        {
                          target: '#Guest.Payment',
                        },
                      ],
                    },
                  },
                  List: {
                    on: {
                      SHIPPING_METHOD_SELECTED: {
                        target: 'Summary',
                      },
                      SHIPPING_ADDRESS_CHANGED: {
                        target: 'NotFetched',
                      },
                      CONTINUE_TO_PAYMENT: [
                        {
                          cond: 'isUsingWallet',
                          target: '#Guest.Payment.StripeWallet',
                        },
                        {
                          target: '#Guest.Payment',
                        },
                      ],
                    },
                  },
                  hist: {
                    type: 'history',
                  },
                },
              },
            },
          },
          Payment: {
            id: 'Payment',
            initial: 'Default',
            states: {
              Default: {
                on: {
                  SPREEDLY_3DS_LIFECYCLE_STARTED: {
                    target: '#Payment.ThreeDSPending',
                  },
                  EDIT_SHIPPING: {
                    target: [
                      '#Guest.Shipping.Address.Edit',
                      '#Guest.Shipping.Methods.hist',
                      '#Guest.Shipping.Contact.hist',
                    ],
                  },
                  EDIT_CONTACT: {
                    target: [
                      '#Guest.Shipping.Address.hist',
                      '#Guest.Shipping.Methods.hist',
                      '#Guest.Shipping.Contact.Edit',
                    ],
                  },
                  EDIT_SHIPPING_METHODS: {
                    target: [
                      '#Guest.Shipping.Address.Summary',
                      '#Guest.Shipping.Methods.List',
                      '#Guest.Shipping.Contact.hist',
                    ],
                  },
                  AMAZONPAY_ON_AUTHORIZE: {
                    actions: ['setAmazonPayToken'],
                    target: '#AmazonPay',
                  },
                  PAYPAL_ON_APPROVED: {
                    actions: ['setPayPalData'],
                    target: '#PayPal',
                  },
                  ORDER_PLACED: {
                    actions: ['setOrderId'],
                    target: '#ThankYou',
                  },
                  ORDER_ERROR: '#Error',
                },
              },
              ThreeDSPending: {
                on: {
                  ORDER_PLACED: {
                    actions: ['setOrderId'],
                    target: '#ThankYou',
                  },
                  ORDER_ERROR: '#Error',
                },
              },
              StripeWallet: {
                on: {
                  EDIT_PAYMENT: {
                    target: 'Default',
                  },
                  EDIT_SHIPPING: {
                    target: [
                      '#Guest.Shipping.Address.Edit',
                      '#Guest.Shipping.Methods.hist',
                      '#Guest.Shipping.Contact.hist',
                    ],
                  },
                  EDIT_CONTACT: {
                    target: [
                      '#Guest.Shipping.Address.hist',
                      '#Guest.Shipping.Methods.hist',
                      '#Guest.Shipping.Contact.Edit',
                    ],
                  },
                  EDIT_SHIPPING_METHODS: {
                    target: [
                      '#Guest.Shipping.Address.hist',
                      '#Guest.Shipping.Methods.List',
                      '#Guest.Shipping.Contact.hist',
                    ],
                  },
                  ORDER_PLACED: {
                    actions: ['setOrderId'],
                    target: '#ThankYou',
                  },
                  ORDER_ERROR: '#Error',
                },
              },
            },
          },
        },
      },
      OneClick: {
        id: 'OneClick',
        initial: 'Loading',
        states: {
          Loading: {
            on: {
              CART_UPDATED: '#OneClick.Start',
              CART_UPDATED_WITH_MISSING_FIELDS: '#OneClick.Start.MissingFields',
            },
          },
          Start: {
            initial: 'Valid',
            states: {
              Valid: {
                on: {
                  SPREEDLY_3DS_LIFECYCLE_STARTED: {
                    target: '#OneClick.Start.ThreeDSPending',
                  },
                  ORDER_PLACED: {
                    actions: ['setOrderId'],
                    target: '#ThankYou',
                  },
                  ORDER_ERROR: '#Error',
                  USER_LOGOUT: '#Guest',
                  EDIT_PAYMENT: '#OneClick.Payment',
                  EDIT_SHIPPING: '#OneClick.ShippingAddress.List',
                  EDIT_BILLING: '#OneClick.BillingAddress.List',
                  ADD_SHIPPING_ADDRESS: '#OneClick.ShippingAddress.Add',
                  ADD_BILLING_ADDRESS: '#OneClick.BillingAddress.Add',
                  CART_UPDATED_WITH_MISSING_FIELDS:
                    '#OneClick.Start.MissingFields',
                  PAYPAL_ON_APPROVED: {
                    actions: ['setPayPalData'],
                    target: '#PayPal',
                  },
                  AMAZONPAY_ON_AUTHORIZE: {
                    actions: ['setAmazonPayToken'],
                    target: '#AmazonPay',
                  },
                },
              },
              MissingFields: {
                on: {
                  CART_UPDATED: '#OneClick.Start.Valid',
                  EDIT_SHIPPING_CANCELED: '#OneClick.ShippingAddress.List',
                },
              },
              hist: {
                type: 'history',
              },
              ThreeDSPending: {
                on: {
                  ORDER_PLACED: {
                    actions: ['setOrderId'],
                    target: '#ThankYou',
                  },
                  ORDER_ERROR: '#Error',
                },
              },
            },
          },
          WalletValidation: {
            initial: 'CheckAll',
            states: {
              CheckAll: {
                on: {
                  WALLET_IS_VALID: '#OneClick.Start.Valid',
                  WALLET_NEEDS_SHIPPING: 'PartialShipping',
                  WALLET_NEEDS_BILLING: 'PartialBilling',
                },
              },
              PartialShipping: {
                initial: 'Edit',
                states: {
                  Edit: {
                    on: {
                      SHIPPING_ADDRESS_SAVED:
                        '#OneClick.WalletValidation.CheckAll',
                    },
                  },
                },
              },
              ChangeShippingMethod: {
                initial: 'Fetching',
                states: {
                  Fetching: {
                    on: {
                      SHIPPING_METHODS_FETCHED: 'Fetched',
                    },
                  },
                  Fetched: {
                    on: {
                      SHIPPING_METHOD_SELECTED:
                        '#OneClick.WalletValidation.CheckAll',
                    },
                  },
                },
              },
              PartialBilling: {
                initial: 'Edit',
                states: {
                  Edit: {
                    on: {
                      BILLING_ADDRESS_SAVED:
                        '#OneClick.WalletValidation.CheckAll',
                    },
                  },
                },
              },
            },
          },
          ShippingAddress: {
            initial: 'List',
            states: {
              List: {
                on: {
                  EDIT_SHIPPING_CANCELED: '#OneClick.Start.hist',
                  ADDRESS_SELECTED: '#OneClick.Start',
                  ADD_SHIPPING_ADDRESS: '#OneClick.ShippingAddress.Add',
                },
              },
              Add: {
                on: {
                  ADD_ADDRESS_CANCEL: '#OneClick.ShippingAddress.List',
                  ADDRESS_ADDED: '#OneClick.Start',
                },
              },
            },
          },
          BillingAddress: {
            initial: 'List',
            states: {
              List: {
                on: {
                  EDIT_BILLING_CANCELED: '#OneClick.Start',
                  ADDRESS_SELECTED: '#OneClick.Start',
                  ADD_BILLING_ADDRESS: '#OneClick.BillingAddress.Add',
                },
              },
              Add: {
                on: {
                  ADD_ADDRESS_CANCEL: '#OneClick.BillingAddress.List',
                  ADDRESS_ADDED: '#OneClick.Start',
                },
              },
            },
          },
          Payment: {
            on: {
              EDIT_PAYMENT_COMPLETE: '#OneClick.Start',
              AMAZONPAY_ON_AUTHORIZE: {
                actions: ['setAmazonPayToken'],
                target: '#AmazonPay',
              },
              PAYPAL_ON_APPROVED: {
                actions: ['setPayPalData'],
                target: '#PayPal',
              },
            },
          },
        },
      },
      PayPal: {
        id: 'PayPal',
        type: 'parallel',
        states: {
          UserStatus: {
            initial: 'CheckAuth',
            states: {
              CheckAuth: {
                id: 'CheckAuth',
                always: [
                  {
                    cond: 'isLoggedIn',
                    target: 'Registered',
                  },
                  {
                    target: 'Guest',
                  },
                ],
              },
              Guest: {
                on: {
                  USER_LOGIN: {
                    actions: ['setCustomer'],
                    target: '#PayPal.UserStatus.Registered',
                  },
                },
              },
              Registered: {
                on: {
                  USER_LOGOUT: {
                    actions: ['setCustomer'],
                    target: '#PayPal.UserStatus.Guest',
                  },
                },
              },
            },
          },
          Checkout: {
            initial: 'Loading',
            on: {
              ORDER_PLACED: {
                actions: ['setOrderId'],
                target: '#ThankYou',
              },
            },
            states: {
              Loading: {
                on: {
                  PAYPAL_DATA_LOADED: {
                    target: '#PayPal.Checkout.Start',
                  },
                  PAYPAL_DATA_LOADED_WITH_MISSING_FIELDS: {
                    target: '#PayPal.Checkout.MissingFields',
                  },
                },
              },
              MissingFields: {
                on: {
                  EDIT_PAYMENT: {
                    target: '#PayPal.Checkout.Payment',
                  },
                  PAYPAL_DATA_LOADED: {
                    target: '#PayPal.Checkout.Start',
                  },
                },
              },
              ShippingEdit: {
                on: {
                  EDIT_PAYMENT: {
                    target: '#PayPal.Checkout.Payment',
                  },
                  PAYPAL_DATA_LOADED: {
                    target: '#PayPal.Checkout.Start',
                  },
                  EDIT_SHIPPING_CANCELED: {
                    target: '#PayPal.Checkout.Start',
                  },
                },
              },
              Start: {
                on: {
                  EDIT_SHIPPING: '#PayPal.Checkout.ShippingEdit',
                  EDIT_PAYMENT: '#PayPal.Checkout.Payment',
                },
              },
              Payment: {
                on: {
                  EDIT_PAYMENT_COMPLETE: '#PayPal.Checkout.Start',
                  PAYPAL_ON_APPROVED: {
                    actions: ['setPayPalData'],
                    target: '#PayPal.Checkout.Loading',
                  },
                  AMAZONPAY_ON_AUTHORIZE: {
                    actions: ['setAmazonPayToken'],
                    target: '#AmazonPay',
                  },
                  PAYPAL_EXIT: {
                    actions: ['setPayPalData'],
                    target: '#PayPal.Checkout.Exit',
                  },
                },
              },
              Exit: {
                always: [
                  {
                    cond: 'isLoggedIn',
                    target: '#OneClick.Start',
                  },
                  {
                    target: '#Guest.Payment',
                  },
                ],
              },
            },
          },
        },
      },
      AmazonPay: {
        id: 'AmazonPay',
        type: 'parallel',
        states: {
          UserStatus: {
            id: 'AmazonPay.UserStatus',
            initial: 'CheckAuth',
            states: {
              CheckAuth: {
                id: 'CheckAuth',
                always: [
                  {
                    cond: 'isLoggedIn',
                    target: 'Registered',
                  },
                  {
                    target: 'Guest',
                  },
                ],
              },
              Guest: {
                id: 'AmazonPay.UserStatus.Guest',
                on: {
                  USER_LOGIN: {
                    actions: ['setCustomer'],
                    target: '#AmazonPay.UserStatus.Registered',
                  },
                },
              },
              Registered: {
                id: 'AmazonPay.UserStatus.Registered',
                on: {
                  USER_LOGOUT: {
                    actions: ['setCustomer'],
                    target: '#AmazonPay.UserStatus.Guest',
                  },
                },
              },
            },
          },
          Checkout: {
            id: 'AmazonPay.Checkout',
            initial: 'Loading',
            on: {
              ORDER_PLACED: {
                actions: ['setOrderId'],
                target: '#ThankYou',
              },
            },
            states: {
              Loading: {
                id: 'AmazonPay.Checkout.Loading',
                on: {
                  CART_UPDATED: '#AmazonPay.Checkout.Start',
                },
              },
              Start: {
                id: 'AmazonPay.Checkout.Start',
                on: {
                  EDIT_PAYMENT: '#AmazonPay.Checkout.Payment',
                },
              },
              Payment: {
                id: 'AmazonPay.Checkout.Payment',
                on: {
                  EDIT_PAYMENT_COMPLETE: '#AmazonPay.Checkout.Start',
                  PAYPAL_ON_APPROVED: {
                    actions: ['setPayPalData'],
                    target: '#PayPal',
                  },
                  AMAZONPAY_ON_AUTHORIZE: {
                    actions: ['setAmazonPayToken'],
                    target: '#AmazonPay.Checkout.Loading',
                  },
                  AMAZONPAY_EXIT: {
                    actions: ['setAmazonPayToken'],
                    target: '#AmazonPay.Checkout.Exit',
                  },
                },
              },
              Exit: {
                id: 'AmazonPay.Checkout.Exit',
                always: [
                  {
                    cond: 'isLoggedIn',
                    target: '#OneClick.Start',
                  },
                  {
                    target: '#Guest.Payment',
                  },
                ],
              },
            },
          },
        },
      },
      Error: {
        id: 'Error',
        on: {
          RETRY: 'Start',
        },
      },
      ThankYou: {
        id: 'ThankYou',
        entry: 'goToOrderFinished',
        type: 'final',
      },
    },
  },
  {
    guards: {
      isLoggedIn: (context): boolean => {
        const isLoggedIn =
          !!context.customer?.id && !context.customer?.isAnonymous;
        return isLoggedIn;
      },
      isUsingPayPal: (context): boolean => {
        const isUsingPayPal =
          !!context.paypalData?.payerID &&
          (!!context.paypalData?.orderID || !!context.paypalData?.paymentID);
        return isUsingPayPal;
      },
      isUsingAmazonPay: (context): boolean => {
        const isUsingAmazonPay = !!context.amazonPayToken;
        return isUsingAmazonPay;
      },
      isUsingWallet: (context): boolean => {
        return !!context.stripeWallet?.token;
      },
      shippingFieldsCompleted: (context, event: CheckoutEvent): boolean => {
        return !!(
          event?.type === 'SHIPPING_METHOD_SELECTED' &&
          event.shippingFieldsCompleted
        );
      },
    },
    actions: {
      logEvent: (context, event: CheckoutEvent): void => {
        // eslint-disable-next-line no-console
        console.log(event.type, ' - ', event);
      },
      // eslint-disable-next-line no-console
      logContext: (context): void => console.log('context - ', context),
      setStoreUrl: assign({
        storeUrl: (context, event: CheckoutEvent) =>
          event.type === 'CONFIG_FETCHING' ? event.storeUrl : null,
      }),
      setCustomer: assign({
        customer: (context, event: CheckoutEvent) =>
          event.type === 'CONFIG_FETCHED' || event.type === 'USER_LOGIN'
            ? event?.customer
            : null,
      }),
      setPayPalData: assign({
        paypalData: (context, event: CheckoutEvent) =>
          event.type === 'CONFIG_FETCHED' || event.type === 'PAYPAL_ON_APPROVED'
            ? event?.paypalParams
            : null,
      }),
      setAmazonPayToken: assign({
        amazonPayToken: (context, event: CheckoutEvent) =>
          event.type === 'CONFIG_FETCHED' ||
          event.type === 'AMAZONPAY_ON_AUTHORIZE'
            ? event?.amazonPayToken
            : null,
      }),
      setStripeWallet: assign({
        stripeWallet: (context, event: CheckoutEvent) =>
          event.type === 'CONFIG_FETCHED' ||
          event.type === 'STRIPE_WALLET_CHOSEN'
            ? event?.stripeWallet
            : null,
      }),
      setOrderId: assign({
        orderId: (context, event: CheckoutEvent) =>
          event.type === 'ORDER_PLACED' ? event.orderId : null,
      }),
      goToOrderFinished: (context): void => {
        WindowService.navigate(
          `${context.storeUrl}/OrderFinished.asp?Order=Finished&sendemail=false&OrderID=${context.orderId}`
        );
      },
    },
  }
);

export default checkoutMachine;
